import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResourceCrudPage, Stack, TabView, type ResourceListArgs, type PaginatedResult } from '@vipso/shared';
import {
  useListChambersQuery,
  useAddUpdateChamberMutation,
  useDeleteChamberMutation,
  useListFloorsQuery,
  useAddUpdateFloorMutation,
  useDeleteFloorMutation,
  useListRacksQuery,
  useAddUpdateRackMutation,
  useDeleteRackMutation,
} from '../api/tenantApi';
import { useAccess } from '../hooks/useAccess';

type NamedRow = { id: string; name: string; chamberId?: string; floorId?: string };

function useChamberList(args: ResourceListArgs) {
  const result = useListChambersQuery(args);
  const data: PaginatedResult<NamedRow> | undefined = result.data?.listChambers
    ? {
        ...result.data.listChambers,
        rows: result.data.listChambers.rows.map((r) => ({ id: r.id, name: r.name })),
      }
    : undefined;
  return { ...result, data };
}

function ChambersTab() {
  const { t } = useTranslation();
  const { canMutate } = useAccess();
  const [save, { isLoading: saving }] = useAddUpdateChamberMutation();
  const [remove] = useDeleteChamberMutation();

  return (
    <ResourceCrudPage<NamedRow>
      title={t('storage.chambers')}
      description={t('storage.chambersDescription')}
      entityLabel={t('storage.chamberEntity')}
      canMutate={canMutate()}
      saving={saving}
      useList={useChamberList}
      fields={[{ key: 'name', label: t('common.name'), required: true }]}
      onSave={(values, editing) => save({ input: { ...values, id: editing?.id } }).unwrap()}
      onDelete={(row) => remove({ id: row.id }).unwrap()}
    />
  );
}

function FloorsTab() {
  const { t } = useTranslation();
  const { canMutate } = useAccess();
  const [save, { isLoading: saving }] = useAddUpdateFloorMutation();
  const [remove] = useDeleteFloorMutation();
  const { data: chambers } = useListChambersQuery({ page: 1, pageLimit: 200 });
  const chamberOptions = useMemo(
    () => (chambers?.listChambers.rows ?? []).map((c) => ({ value: c.id, label: c.name })),
    [chambers],
  );

  function useFloorList(args: ResourceListArgs) {
    const result = useListFloorsQuery(args);
    const data: PaginatedResult<NamedRow> | undefined = result.data?.listFloors
      ? {
          ...result.data.listFloors,
          rows: result.data.listFloors.rows.map((r) => ({
            id: r.id,
            name: r.name,
            chamberId: r.chamberId,
          })),
        }
      : undefined;
    return { ...result, data };
  }

  return (
    <ResourceCrudPage<NamedRow>
      title={t('storage.floors')}
      description={t('storage.floorsDescription')}
      entityLabel={t('storage.floorEntity')}
      canMutate={canMutate()}
      saving={saving}
      useList={useFloorList}
      fields={[
        { key: 'name', label: t('common.name'), required: true },
        {
          key: 'chamberId',
          label: t('orders.chamber'),
          required: true,
          options: chamberOptions,
        },
      ]}
      onSave={(values, editing) =>
        save({ input: { ...values, id: editing?.id, chamberId: values.chamberId } }).unwrap()
      }
      onDelete={(row) => remove({ id: row.id }).unwrap()}
      subtitleOf={(row) => {
        const c = chamberOptions.find((o) => o.value === row.chamberId);
        return c ? t('storage.chamberOf', { name: c.label }) : undefined;
      }}
    />
  );
}

function RacksTab() {
  const { t } = useTranslation();
  const { canMutate } = useAccess();
  const [save, { isLoading: saving }] = useAddUpdateRackMutation();
  const [remove] = useDeleteRackMutation();
  const { data: floors } = useListFloorsQuery({ page: 1, pageLimit: 200 });
  const floorOptions = useMemo(
    () => (floors?.listFloors.rows ?? []).map((f) => ({ value: f.id, label: f.name })),
    [floors],
  );

  function useRackList(args: ResourceListArgs) {
    const result = useListRacksQuery(args);
    const data: PaginatedResult<NamedRow> | undefined = result.data?.listRacks
      ? {
          ...result.data.listRacks,
          rows: result.data.listRacks.rows.map((r) => ({
            id: r.id,
            name: r.name,
            floorId: r.floorId,
          })),
        }
      : undefined;
    return { ...result, data };
  }

  return (
    <ResourceCrudPage<NamedRow>
      title={t('storage.racks')}
      description={t('storage.racksDescription')}
      entityLabel={t('storage.rackEntity')}
      canMutate={canMutate()}
      saving={saving}
      useList={useRackList}
      fields={[
        { key: 'name', label: t('common.name'), required: true },
        {
          key: 'floorId',
          label: t('orders.floor'),
          required: true,
          options: floorOptions,
        },
      ]}
      onSave={(values, editing) =>
        save({ input: { ...values, id: editing?.id, floorId: values.floorId } }).unwrap()
      }
      onDelete={(row) => remove({ id: row.id }).unwrap()}
      subtitleOf={(row) => {
        const f = floorOptions.find((o) => o.value === row.floorId);
        return f ? t('storage.floorOf', { name: f.label }) : undefined;
      }}
    />
  );
}

export function StorageLocationsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('chambers');

  return (
    <Stack gap="md">
      <TabView
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'chambers', title: t('storage.chambers'), content: <ChambersTab /> },
          { value: 'floors', title: t('storage.floors'), content: <FloorsTab /> },
          { value: 'racks', title: t('storage.racks'), content: <RacksTab /> },
        ]}
      />
    </Stack>
  );
}
